/**
 * sky_view — what stands in a system's sky beyond the system (the natural object layer, 2026-10-07): a star cluster, a
 * distant companion sun, a guest star. The populations, the light curves and their sources are in sky_look.ts (pure).
 *
 * EVERYTHING HERE IS A SIZED SPRITE ON THE SKY SPHERE. A cluster star, a guest star, the companion's disc: instanced
 * quads whose corners are offset in CLIP space by a size in pixels (three's `Points` is one pixel on both backends),
 * seated `SKY_R` from the ship — inside the 3,600 km far plane, behind the sun rig (2,500 km), every far-body impostor
 * (100 km) and the black-hole view's cap. They are drawn before everything else transparent, so a nebula in front of
 * them is composited over them.
 *  - A core never drawn under `CORE_MIN_PX` of the pass's own pixels, with its light CONSERVED (a sub-pixel dot
 *    scintillates as the ship turns; the same total light over a wider core does not).
 *  - A thing at a PLACE (the embedded cluster inside its nebula, the companion sun) is seated by the far-body rule at
 *    SKY_R instead of 100 km: on its view ray from the ship, scaled by SKY_R / distance — its parallax against the
 *    nebula it belongs to is exact wherever the ship flies.
 *  - No light is added (THE LIGHT-SET IDENTITY LAW): a second sun lights nothing.
 */
import * as THREE from 'three/webgpu';
import { Fn, attribute, cameraProjectionMatrix, modelViewMatrix, positionGeometry, screenSize, uniform, uv, vec3, vec4, float, int, length, exp, max, min, pow, smoothstep, sqrt, varyingProperty } from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { deferDisposeTree } from '../../defer_dispose.js';
import { skyNebulaeFor } from '../nebula_look.js';
import type { NaturalEnv, SkyView } from './natural_api.js';
import type { SkyMakeup, SystemMakeup } from './natural_types.js';
import { STAR_STRIDE, TRANSIENT_LABEL, clusterStars, distantSunRadiusM, starRgb, transientAt, transientLight, type Transient } from './sky_look.js';
import { starChartHex } from './star_view.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

/** The sky sphere's radius about the ship (m). */
export const SKY_R = 3_000_000;
/** A sprite's quad reaches this many core radii (room for the halo). */
const SPRITE_EXT = 4;
/** The smallest core radius drawn, in the pass's own pixels. */
export const CORE_MIN_PX = 1.15;
/** The companion's quad reaches this many disc radii (its glow). */
const SUN_EXT = 7;

let spriteMat: THREE.MeshBasicNodeMaterial | null = null;
/**
 * The one sprite material (session-shared). Per instance: `aStar` = centre (mesh-local m) + the core radius — positive:
 * pixels at a 1080-row frame; negative: an ANGLE in radians (an unresolved glow keeps its size on the sky); `aTint` =
 * linear rgb × flux.
 */
function spriteMaterial(): THREE.MeshBasicNodeMaterial {
  if (spriteMat) return spriteMat;
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
  markMrtOverlay(mat);
  mat.fog = false;
  mat.userData.shared = true;
  mat.name = 'natural-sky-sprite';
  const vTint = varyingProperty('vec3', 'vSkyTint');
  mat.vertexNode = Fn(() => {
    const st = attribute('aStar', 'vec4');
    const clip = (cameraProjectionMatrix.mul(modelViewMatrix.mul(vec4(st.xyz, 1.0))) as N).toVar();
    const p11 = cameraProjectionMatrix.element(int(1)).y; // 1 / tan(fov / 2)
    // the core radius in this pass's pixels: a star's follows the frame's height, a glow's its angle (arithmetic, no branch)
    const want = (max(st.w, float(0)).mul(screenSize.y).div(1080)).add(max(st.w.negate(), float(0)).mul(p11).mul(screenSize.y).mul(0.5)).toVar();
    const used = max(want, float(CORE_MIN_PX)).toVar();
    // WHY the gain: a core widened to the pixel floor keeps its total light, or every faint star would brighten
    const gain = want.div(used).mul(want.div(used));
    vTint.assign((attribute('aTint', 'vec3') as N).mul(gain));
    const off = positionGeometry.xy.mul(used.mul(4 * SPRITE_EXT)).div(screenSize).mul(clip.w);
    return vec4(clip.xy.add(off), clip.zw);
  })();
  mat.colorNode = Fn(() => {
    const r = length(uv().sub(0.5)).mul(2 * SPRITE_EXT); // in core radii
    const core = exp(r.mul(r).negate().div(0.72));
    const halo = exp(r.negate().div(0.9)).mul(0.045);
    const edge = float(1).sub(smoothstep(SPRITE_EXT * 0.7, SPRITE_EXT, r));
    return (vTint as N).mul(core.add(halo).mul(edge));
  })();
  spriteMat = mat;
  return mat;
}

/** An instanced sprite mesh over `aStar` / `aTint` arrays (the arrays are kept: a guest star rewrites its one row). */
function spriteMesh(name: string, star: Float32Array, tint: Float32Array): THREE.Mesh {
  const quad = new THREE.PlaneGeometry(1, 1);
  const geo = new THREE.InstancedBufferGeometry();
  geo.setIndex(quad.getIndex());
  geo.setAttribute('position', quad.getAttribute('position'));
  geo.setAttribute('uv', quad.getAttribute('uv'));
  geo.setAttribute('aStar', new THREE.InstancedBufferAttribute(star, 4));
  geo.setAttribute('aTint', new THREE.InstancedBufferAttribute(tint, 3));
  geo.instanceCount = star.length / 4;
  const mesh = new THREE.Mesh(geo, spriteMaterial());
  mesh.name = name;
  mesh.frustumCulled = false;
  mesh.renderOrder = -20; // before every other transparent thing: the sky is behind all of it
  return mesh;
}

/** A companion sun's own material: a limb-darkened disc and its glow (one per view; freed with it). */
function distantSunMesh(angExtRad: { value: number }, rgb: [number, number, number]): THREE.Mesh {
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
  markMrtOverlay(mat);
  mat.fog = false;
  mat.name = 'natural-distant-sun';
  const uExt = uniform(0.01); // the quad's half-size as an angle (rad)
  const uCol = uniform(new THREE.Color(rgb[0], rgb[1], rgb[2]));
  mat.vertexNode = Fn(() => {
    const clip = (cameraProjectionMatrix.mul(modelViewMatrix.mul(vec4(0, 0, 0, 1.0))) as N).toVar();
    const p11 = cameraProjectionMatrix.element(int(1)).y;
    const px = max((uExt as N).mul(p11).mul(screenSize.y).mul(0.5), float(CORE_MIN_PX * SUN_EXT));
    const off = positionGeometry.xy.mul(px.mul(4)).div(screenSize).mul(clip.w);
    return vec4(clip.xy.add(off), clip.zw);
  })();
  mat.colorNode = Fn(() => {
    const r = length(uv().sub(0.5)).mul(2 * SUN_EXT).toVar(); // in disc radii
    const mu = sqrt(max(float(0), float(1).sub(r.mul(r))));
    const limb = float(0.3).add(mu.mul(0.94)).sub(mu.mul(mu).mul(0.24)); // the sun rig's quadratic law
    const disc = limb.mul(float(1).sub(smoothstep(0.92, 1.04, r))).mul(6);
    const glow = pow(max(r, float(1)), float(-2.6)).mul(smoothstep(0.9, 1.1, r)).mul(0.9);
    const edge = float(1).sub(smoothstep(SUN_EXT * 0.6, SUN_EXT, r));
    return vec3(uCol as N).mul(disc.add(glow.mul(edge)));
  })();
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
  mesh.name = 'natural-distant-sun';
  mesh.frustumCulled = false;
  mesh.renderOrder = -19;
  mesh.onBeforeRender = () => { (uExt as unknown as { value: number }).value = Number.isFinite(angExtRad.value) ? angExtRad.value : 0.01; };
  return mesh;
}

/** Two unit vectors spanning the plane across `d` (unit). */
function tangents(d: THREE.Vector3): [THREE.Vector3, THREE.Vector3] {
  const up = Math.abs(d.y) < 0.92 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
  const e1 = new THREE.Vector3().crossVectors(up, d).normalize();
  return [e1, new THREE.Vector3().crossVectors(d, e1).normalize()];
}

interface Seat { mesh: THREE.Mesh; /** A place (absolute m): seated by the far rule at SKY_R. Null = a direction: it rides the ship as built. */ abs: THREE.Vector3 | null }

export function createSkyView(env: NaturalEnv, makeup: SystemMakeup): SkyView | null {
  const root = env.root();
  if (!root) return null;
  const seats: Seat[] = [];
  const stats = { clusters: [] as Array<{ kind: string; stars: number; angDeg: number; place: boolean }>, distant: null as null | { kind: string; angDeg: number }, guest: null as null | { kind: string; ageS: number; flux: number } };
  const add = (mesh: THREE.Mesh, abs: THREE.Vector3 | null): void => { root.add(mesh); seats.push({ mesh, abs }); };

  // ── clusters ──
  const cluster = (sk: SkyMakeup): void => {
    const cs = clusterStars(sk.kind, sk.seed, env.level);
    const n = cs.count + (cs.glow ? 1 : 0);
    const star = new Float32Array(n * 4), tint = new Float32Array(n * 3);
    // an embedded cluster is INSIDE its nebula: at the nebula's place, a third of its radius across
    const neb = sk.kind === 'embedded_cluster' ? skyNebulaeFor(makeup.systemId, makeup.skyNebula)[0] ?? null : null;
    const dir = new THREE.Vector3(sk.dir[0], sk.dir[1], sk.dir[2]).normalize();
    if (neb) dir.set(neb.pos[0], neb.pos[1], neb.pos[2]).normalize();
    const [e1, e2] = tangents(dir);
    const physR = neb ? neb.radius * 0.34 : 0;
    const p = new THREE.Vector3();
    for (let i = 0; i < cs.count; i++) {
      const o = i * STAR_STRIDE, x = cs.data[o], y = cs.data[o + 1], z = cs.data[o + 2];
      if (neb) p.set(0, 0, 0).addScaledVector(e1, x * physR).addScaledVector(e2, y * physR).addScaledVector(dir, z * physR);
      else p.copy(dir).addScaledVector(e1, x * sk.angR).addScaledVector(e2, y * sk.angR).normalize().multiplyScalar(SKY_R);
      star.set([p.x, p.y, p.z, cs.data[o + 6]], i * 4);
      tint.set([cs.data[o + 3], cs.data[o + 4], cs.data[o + 5]], i * 3);
    }
    if (cs.glow) {
      const i = cs.count;
      if (neb) p.set(0, 0, 0); else p.copy(dir).multiplyScalar(SKY_R);
      // a glow's size is an ANGLE (negative = radians); at a place, the angle its radius spans from the system's heart
      const ang = neb ? Math.atan2(physR * cs.glow.radius, Math.hypot(neb.pos[0], neb.pos[1], neb.pos[2])) : sk.angR * cs.glow.radius;
      star.set([p.x, p.y, p.z, -ang / SPRITE_EXT * 2.2], i * 4);
      tint.set(cs.glow.rgb, i * 3);
    }
    add(spriteMesh(`natural-sky-${sk.kind}`, star, tint), neb ? new THREE.Vector3(neb.pos[0], neb.pos[1], neb.pos[2]) : null);
    stats.clusters.push({ kind: sk.kind, stars: cs.count, angDeg: Math.round((neb ? Math.atan2(physR, Math.hypot(neb.pos[0], neb.pos[1], neb.pos[2])) : sk.angR) * 1800 / Math.PI) / 10, place: !!neb });
  };
  for (const sk of makeup.sky) cluster(sk);

  // ── the distant companion sun ──
  const sunAng = { value: 0.01 };
  let sunR = 0;
  if (makeup.distant) {
    const hex = starChartHex(makeup.distant.kind);
    const lin = new THREE.Color().setHex(hex, THREE.SRGBColorSpace);
    sunR = distantSunRadiusM(makeup.distant.kind);
    const m = distantSunMesh(sunAng, [lin.r, lin.g, lin.b]);
    add(m, new THREE.Vector3(makeup.distant.pos[0], makeup.distant.pos[1], makeup.distant.pos[2]));
  }

  // ── the guest star (one row, rewritten while it shines) ──
  // two rows: the star itself, and the glare a very bright point makes in the eye (a soft round glow, no spikes: spikes are a telescope's)
  const gStar = new Float32Array(8), gTint = new Float32Array(6);
  const guest = spriteMesh('natural-sky-guest', gStar, gTint);
  guest.visible = false;
  add(guest, null);
  let guestSlot = -1, toasted = -1;
  const guestFrame = (now: number): void => {
    const t: Transient | null = transientAt(now);
    if (!t) { guest.visible = false; stats.guest = null; return; }
    const l = transientLight(t.kind, t.ageS);
    if (!(l.flux > 1e-3)) { guest.visible = false; stats.guest = null; return; }
    if (t.slot !== guestSlot) {
      guestSlot = t.slot;
      gStar[0] = gStar[4] = t.dir[0] * SKY_R; gStar[1] = gStar[5] = t.dir[1] * SKY_R; gStar[2] = gStar[6] = t.dir[2] * SKY_R;
    }
    // the core grows a little with the light (the eye's irradiation), the rest is flux
    const k = Math.sqrt(Math.min(1, l.flux / 46));
    gStar[3] = 1.6 + 1.8 * k;
    gTint[0] = l.rgb[0] * l.flux; gTint[1] = l.rgb[1] * l.flux; gTint[2] = l.rgb[2] * l.flux;
    gStar[7] = -(0.005 + 0.027 * k); // the glare's radius is an ANGLE: ~1.5° at a supernova's best
    const g = 0.02 * Math.min(l.flux, 46) * k;
    gTint[3] = l.rgb[0] * g; gTint[4] = l.rgb[1] * g; gTint[5] = l.rgb[2] * g;
    (guest.geometry.getAttribute('aStar') as THREE.InstancedBufferAttribute).needsUpdate = true;
    (guest.geometry.getAttribute('aTint') as THREE.InstancedBufferAttribute).needsUpdate = true;
    guest.visible = true;
    stats.guest = { kind: t.kind, ageS: Math.round(t.ageS), flux: Math.round(l.flux * 100) / 100 };
    // say so once, while it is still rising or near its best (a late arrival is not told about embers)
    if (toasted !== t.slot && t.ageS < 240) { toasted = t.slot; env.toast(`✦ A guest star in the sky — ${TRANSIENT_LABEL[t.kind]}, far beyond this system.`); }
  };

  const ship = new THREE.Vector3(), d = new THREE.Vector3();
  return {
    frame(_dt: number, nowS: number): void {
      const o = env.origin(), s = env.shipAbs();
      ship.set(s.x - o.x, s.y - o.y, s.z - o.z);
      for (const st of seats) {
        if (!st.abs) { st.mesh.position.copy(ship); continue; }
        d.set(st.abs.x - s.x, st.abs.y - s.y, st.abs.z - s.z);
        const dist = Math.max(1, d.length()), k = SKY_R / dist;
        st.mesh.position.copy(ship).addScaledVector(d, k);
        st.mesh.scale.setScalar(k);
        if (st.mesh.name === 'natural-distant-sun') {
          const ang = Math.asin(Math.min(0.5, sunR / dist));
          sunAng.value = ang * SUN_EXT;
          stats.distant = { kind: makeup.distant?.kind ?? '', angDeg: Math.round(ang * 1800 / Math.PI) / 10 };
        }
      }
      guestFrame(nowS);
    },
    state: () => ({ clusters: stats.clusters, distant: stats.distant, guest: stats.guest, meshes: seats.length }),
    dispose(): void {
      // detach now, free after the submit window (the sprite material is session-shared and stays)
      for (const st of seats) deferDisposeTree(st.mesh);
      seats.length = 0;
    },
  };
}

/** The colour of a kelvin temperature as linear rgb (re-exported for the bed). */
export { starRgb };
